import { useAtomValue } from "jotai";
import type { NextPage } from "next";
import { useRouter } from "next/router";
import { useEffect } from "react";

import { theme } from "shared/lib/styles";

import { useTopBannerAdSlot } from "scmp-app/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { AppFooter } from "scmp-app/components/app-footer";
import { GamesLoading } from "scmp-app/components/games/loading";
import { GameLoadingDuration } from "scmp-app/components/games/loading/consts";
import { useLoadingTimer } from "scmp-app/components/games/loading/hooks";
import { GameType } from "scmp-app/components/games/types";
import { loginDialogAtom } from "scmp-app/components/login-dialog/atoms";
import { PageSeo } from "scmp-app/components/meta";
import { trackingAtom } from "scmp-app/components/tracking";
import { useGameIframe, useWordSearchSeo } from "scmp-app/pages/games/hooks";
import { GamePageSchema } from "scmp-app/pages/games/schema";
import { IframeContainer, IframeWrapper } from "scmp-app/pages/games/styles";

import { AdsContainer, PageContainer, StyledAdSlot } from "./styles";

const WordSearchGamePage: NextPage = () => {
  const router = useRouter();
  const { date } = router.query;
  const queryString = router.asPath.split("?")[1] ?? "";

  const { iframeId } = useGameIframe({
    date,
    path: "games/word-search",
    queryString,
  });

  const isLoading = useLoadingTimer(GameLoadingDuration);
  const adZone = `game/wordsearch_gaming`;
  const seo = useWordSearchSeo();

  useTopBannerAdSlot({
    desktop: {
      adUnit: "d_banner1",
      autoRefreshOptions: {
        intervalRefreshOptions: {
          isDisable: false,
        },
        scrollBackRefreshOptions: {
          isDisable: true,
        },
      },
      sizes: [
        [970, 250],
        [728, 90],
        [970, 90],
      ],
      targeting: {
        paid: TargetingPaidType.NotArticle,
      },
      zone: adZone,
    },
    mobile: {
      adUnit: "m_banner3",
      autoRefreshOptions: {
        intervalRefreshOptions: {
          isDisable: false,
        },
        scrollBackRefreshOptions: {
          isDisable: true,
        },
      },
      sizes: [
        [300, 100],
        [320, 100],
        [300, 50],
        [320, 50],
      ],
      targeting: {
        paid: TargetingPaidType.NotArticle,
      },
      zone: adZone,
    },
  });

  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  const loginState = useAtomValue(loginDialogAtom);
  useEffect(() => {
    if (!isLoading) {
      const iframeContainer = document.querySelector(`#${iframeId}`);
      if (iframeContainer?.childNodes[0].nodeName === "IFRAME" && !loginState.isVisible) {
        // Not focus to the game when login popup is showing
        const iframe = iframeContainer?.childNodes[0] as HTMLIFrameElement | null;
        iframe?.focus();
        const handleIframeBlur = () => {
          iframe?.focus();
        };

        iframe?.addEventListener("blur", handleIframeBlur);

        return () => {
          iframe?.removeEventListener("blur", handleIframeBlur);
        };
      }
    }
    return;
  }, [isLoading, iframeId, loginState.isVisible]);

  return (
    <PageContainer>
      <GamePageSchema />
      <PageSeo {...seo} />
      <IframeContainer $isLoading={isLoading}>
        <IframeWrapper id={iframeId} />
        <GamesLoading
          date={date as string}
          duration={GameLoadingDuration}
          isLoading={isLoading}
          theme={GameType.WordSearch}
        />
      </IframeContainer>

      {!isLoading && (
        <AdsContainer>
          <StyledAdSlot
            adUnit="d_banner2"
            breakpoint={theme.breakpoints.up("tablet")}
            sizes={[
              [728, 90],
              [970, 90],
            ]}
            targeting={{
              paid: TargetingPaidType.NotArticle,
            }}
            withLabel={true}
            zone={adZone}
          />
        </AdsContainer>
      )}
      {!isLoading && <AppFooter variant="games" />}
    </PageContainer>
  );
};

const pageProps = {
  appBarConfiguration: {
    adSlotVariants: {
      large: {
        height: 250,
        padding: 20,
      },
      small: {
        height: 100,
        padding: 10,
      },
    },
    hasDesktopAd: true,
    hasMobileAd: true,
    variant: "scmp/slim",
  },
  contentConfiguration: {
    responsivePadding: {
      desktopUp: 12,
      tabletUp: 0,
    },
  },
  pageConfiguration: {
    responsivePadding: {
      tabletUp: 24,
    },
  },
};

WordSearchGamePage.getInitialProps = () => pageProps;

export default WordSearchGamePage;
